import ComparePage, { CompareRow, CompareFaq, Reason } from '@comp/compare/compare-template'
import AssetDownload from '@comp/product/asset-download'

interface PageContent {
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  reasons: Reason[]
  tableTitle: [string, string]
  tableBody: string
  rows: CompareRow[]
  faqs: CompareFaq[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    seoTitle: 'CyberArk Alternative: Monofor | PAM without the complexity',
    seoDescription:
      'Looking for a CyberArk alternative? Monopam delivers vault, session recording, and just-in-time access, with IAM and IGA in the same platform, self-hosted or cloud, at per-session pricing.',
    heroEyebrow: 'CyberArk alternative',
    heroTitle: ['Enterprise PAM.', 'Without the enterprise drag.'],
    heroBody:
      'Monopam covers what privileged access actually needs (encrypted vault, session recording, rotation, just-in-time approvals) inside a platform that also handles IAM and IGA. Deployed in minutes, priced per concurrent session, support included.',
    reasons: [
      {
        title: 'One platform, not a product family',
        body: 'CyberArk grew through acquisitions; the seams show in consoles, SKUs, and renewals. Monofor was built as one platform: privileged access, identity, and governance share a console and a data model.',
      },
      {
        title: 'Pricing that matches usage',
        body: 'PAM is licensed per concurrent session, not per admin, per target, or per vaulted credential. Identity starts at $4 per user per month, published right on our pricing page.',
      },
      {
        title: 'Minutes to production, not quarters',
        body: 'Browser-based RDP and SSH mean no agent fleet to roll out. Resource discovery pulls from AD, Azure, and AWS. Most teams record their first privileged session the same day they install.',
      },
    ],
    tableTitle: ['Monofor vs CyberArk,', 'feature by feature.'],
    tableBody:
      'Where it matters for day-to-day privileged access, the platforms are at parity; the differences are in scope, pricing model, and how much operational weight you take on.',
    rows: [
      {
        feature: 'IAM + PAM + IGA in one product',
        detail: 'One vendor, one console, one bill',
        monofor: true,
        competitor: 'Assembled via acquisitions',
      },
      {
        feature: 'Privileged credential vault',
        detail: 'Encrypted storage, credential history, rotation',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Session recording',
        detail: 'Video + keystroke transcript for RDP & SSH',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Browser-based RDP / SSH access',
        detail: 'No client installs or agent fleet',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Just-in-time privileged access',
        detail: 'Time-bound, approval-gated grants',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Enterprise SSO + MFA included',
        detail: '7,000+ app catalog, passkeys, adaptive MFA',
        monofor: true,
        competitor: 'Separate products',
      },
      {
        feature: 'Self-hosted deployment',
        detail: 'Your infrastructure, your data',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Per-concurrent-session PAM pricing',
        detail: 'Not per admin, per target, or per credential',
        monofor: true,
        competitor: 'Per-user / quote-based',
      },
      {
        feature: 'Public entry pricing',
        detail: 'IAM from $4 per user / month',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Deploy in minutes without professional services',
        monofor: true,
        competitor: 'PS engagement typical',
      },
      {
        feature: 'Kubernetes & OpenShift access (native kubectl / oc)',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Database sessions with SQL policy & dynamic masking',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Certificate lifecycle management (CLM) built in',
        monofor: true,
        competitor: 'Separate platform (Venafi)',
      },
      {
        feature: 'Vendor privileged access (VPAM) self-hosted / air-gapped',
        detail: 'Approval-gated invitations, time-boxed vendor accounts, recorded sessions',
        monofor: true,
        competitor: 'SaaS-delivered add-on',
      },
      {
        feature: 'Customer support included',
        monofor: true,
        competitor: 'Tiered / add-on',
      },
    ],
    faqs: [
      {
        q: 'Is Monopam a full CyberArk replacement?',
        a: 'For the vast majority of teams (vaulting credentials, brokering and recording privileged sessions, rotating passwords, and enforcing just-in-time access), yes, and at a fraction of the operational complexity. Organizations with deep application-to-application secrets-management estates should evaluate their specific workflows during the trial.',
      },
      {
        q: 'How is migration from CyberArk handled?',
        a: 'Credentials are imported into the Monopam vault, and resources are discovered automatically from Active Directory, Azure, and AWS. Because Monopam is agentless for session access (browser-based RDP/SSH), there is no endpoint rollout to coordinate.',
      },
      {
        q: 'What does per-concurrent-session pricing mean in practice?',
        a: 'You license the number of privileged sessions running at the same time, not the number of admins, servers, or vaulted credentials. Most teams run far fewer concurrent sessions than they have administrators, which is why this model typically undercuts per-user PAM pricing significantly.',
      },
      {
        q: 'Do I have to buy the whole platform?',
        a: 'No. Monosign (IAM), Monopam (PAM), and Monosync (IGA) are licensed as modules. Start with PAM and add identity or governance later; they share one console and data model when you do.',
      },
      {
        q: 'Can I run it in my own datacenter?',
        a: 'Yes. Monofor deploys self-hosted on your infrastructure or as SaaS: same product, same price. Data-sovereignty and regulated environments are a core design target, not an afterthought.',
      },
    ],
  },
  tr: {
    seoTitle: 'CyberArk Alternatifi: Monofor | Karmaşasız PAM',
    seoDescription:
      'CyberArk alternatifi mi arıyorsunuz? Monopam; kasa, oturum kaydı ve tam zamanında erişimi (IAM ve IGA ile aynı platformda, kendi altyapınızda veya bulutta, oturum başına fiyatlandırmayla) sunar.',
    heroEyebrow: 'CyberArk alternatifi',
    heroTitle: ['Kurumsal PAM.', 'Kurumsal hantallık olmadan.'],
    heroBody:
      'Monopam, ayrıcalıklı erişimin gerçekten ihtiyaç duyduğu her şeyi kapsar (şifreli kasa, oturum kaydı, rotasyon, tam zamanında onaylar); üstelik IAM ve IGA’yı da barındıran bir platformun içinde. Günler içinde kurulur, eşzamanlı oturum başına fiyatlandırılır, destek dahildir.',
    reasons: [
      {
        title: 'Ürün ailesi değil, tek platform',
        body: 'CyberArk satın almalarla büyüdü; dikiş yerleri konsollarda, SKU’larda ve yenilemelerde kendini gösterir. Monofor tek platform olarak tasarlandı: ayrıcalıklı erişim, kimlik ve yönetişim aynı konsolu ve aynı veri modelini paylaşır.',
      },
      {
        title: 'Kullanımla örtüşen fiyatlandırma',
        body: 'PAM eşzamanlı oturum başına lisanslanır; yönetici, hedef veya kasadaki kimlik bilgisi başına değil. Kimlik tarafı kullanıcı başına aylık 4 $’dan başlar ve fiyatlandırma sayfamızda açıkça yayımlanır.',
      },
      {
        title: 'Çeyrekler değil, dakikalar içinde üretimde',
        body: 'Tarayıcı tabanlı RDP ve SSH sayesinde dağıtılacak bir ajan filosu yoktur. Kaynak keşfi AD, Azure ve AWS’den beslenir. Çoğu ekip ilk ayrıcalıklı oturumunu kurulumla aynı gün kaydeder.',
      },
    ],
    tableTitle: ['Monofor ve CyberArk,', 'özellik özellik.'],
    tableBody:
      'Günlük ayrıcalıklı erişim açısından önemli olan yerlerde platformlar başa baş; farklar kapsamda, fiyatlandırma modelinde ve üstlendiğiniz operasyonel yükte.',
    rows: [
      {
        feature: 'IAM + PAM + IGA tek üründe',
        detail: 'Tek tedarikçi, tek konsol, tek fatura',
        monofor: true,
        competitor: 'Satın almalarla birleştirilmiş',
      },
      {
        feature: 'Ayrıcalıklı kimlik bilgisi kasası',
        detail: 'Şifreli depolama, kimlik bilgisi geçmişi, rotasyon',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Oturum kaydı',
        detail: 'RDP ve SSH için video + tuş vuruşu dökümü',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Tarayıcı tabanlı RDP / SSH erişimi',
        detail: 'İstemci kurulumu veya ajan filosu yok',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Tam zamanında ayrıcalıklı erişim',
        detail: 'Süre sınırlı, onaya bağlı yetkilendirmeler',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Kurumsal SSO + MFA dahil',
        detail: '7.000+ uygulama kataloğu, passkey, uyarlanabilir MFA',
        monofor: true,
        competitor: 'Ayrı ürünler',
      },
      {
        feature: 'Kendi altyapınızda kurulum',
        detail: 'Sizin altyapınız, sizin veriniz',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Eşzamanlı oturum başına PAM fiyatlandırması',
        detail: 'Yönetici, hedef veya kimlik bilgisi başına değil',
        monofor: true,
        competitor: 'Kullanıcı başına / teklife dayalı',
      },
      {
        feature: 'Açık başlangıç fiyatı',
        detail: 'IAM kullanıcı başına aylık 4 $’dan başlar',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Profesyonel hizmet olmadan dakikalar içinde kurulum',
        monofor: true,
        competitor: 'Genellikle PS projesi gerekir',
      },
      {
        feature: 'Kubernetes ve OpenShift erişimi (yerel kubectl / oc)',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'SQL politikası ve dinamik maskelemeli veritabanı oturumları',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Yerleşik sertifika yaşam döngüsü yönetimi (CLM)',
        monofor: true,
        competitor: 'Ayrı platform (Venafi)',
      },
      {
        feature: 'Kendi altyapınızda / kapalı ağda tedarikçi ayrıcalıklı erişimi (VPAM)',
        detail: 'Onay kapılı davetler, süreli tedarikçi hesapları, kayıtlı oturumlar',
        monofor: true,
        competitor: 'SaaS olarak sunulan ek ürün',
      },
      {
        feature: 'Müşteri desteği dahil',
        monofor: true,
        competitor: 'Kademeli / ek ücretli',
      },
    ],
    faqs: [
      {
        q: 'Monopam, CyberArk’ın yerini tam olarak alabilir mi?',
        a: 'Ekiplerin büyük çoğunluğu için (kimlik bilgilerini kasada saklamak, ayrıcalıklı oturumları aracılamak ve kaydetmek, parolaları döndürmek ve tam zamanında erişimi uygulamak) evet, hem de operasyonel karmaşanın çok küçük bir kısmıyla. Uygulamadan uygulamaya derin gizli anahtar yönetimi ortamına sahip kurumlar, deneme sürecinde kendi iş akışlarını özel olarak değerlendirmelidir.',
      },
      {
        q: 'CyberArk’tan geçiş nasıl yürütülür?',
        a: 'Kimlik bilgileri Monopam kasasına aktarılır; kaynaklar Active Directory, Azure ve AWS üzerinden otomatik olarak keşfedilir. Monopam oturum erişiminde ajansız (tarayıcı tabanlı RDP/SSH) çalıştığı için koordine edilmesi gereken bir uç nokta dağıtımı yoktur.',
      },
      {
        q: 'Eşzamanlı oturum başına fiyatlandırma pratikte ne anlama gelir?',
        a: 'Aynı anda çalışan ayrıcalıklı oturum sayısını lisanslarsınız; yönetici, sunucu veya kasadaki kimlik bilgisi sayısını değil. Çoğu ekip, sahip olduğu yönetici sayısından çok daha az eşzamanlı oturum çalıştırır; bu model bu yüzden kullanıcı başına PAM fiyatlandırmasının genellikle belirgin biçimde altında kalır.',
      },
      {
        q: 'Platformun tamamını satın almak zorunda mıyım?',
        a: 'Hayır. Monosign (IAM), Monopam (PAM) ve Monosync (IGA) modül olarak lisanslanır. PAM ile başlayın; kimlik veya yönetişimi daha sonra ekleyin; eklediğinizde tek konsolu ve tek veri modelini paylaşırlar.',
      },
      {
        q: 'Kendi veri merkezimde çalıştırabilir miyim?',
        a: 'Evet. Monofor kendi altyapınızda self-hosted olarak veya SaaS olarak kurulur: aynı ürün, aynı fiyat. Veri egemenliği ve regülasyona tabi ortamlar sonradan akla gelen bir ek değil, temel tasarım hedefidir.',
      },
    ],
  },
}

type Props = { locale: string }

export default function CyberArkAlternativePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <ComparePage
      leadMagnet={
        <AssetDownload
          asset="pam-rfp"
          title={{
            en: 'Evaluating PAM vendors? Take the 50 questions.',
            tr: 'PAM tedarikçisi mi değerlendiriyorsunuz? 50 soruyu alın.',
          }}
          copy={{
            en: 'A vendor-neutral RFP template: 50 questions across vault, sessions, JIT, Kubernetes, databases, certificates, EPM, architecture, integrations, and commercials, with a scoring rubric and the red flags to watch for.',
            tr: 'Tedarikçi-bağımsız RFP şablonu: kasa, oturumlar, JIT, Kubernetes, veritabanları, sertifikalar, EPM, mimari, entegrasyonlar ve ticari koşullarda 50 soru; puanlama cetveli ve dikkat edilecek kırmızı bayraklarla.',
          }}
        />
      }
      competitor="CyberArk"
      locale={locale}
      bilingual
      path="/compare/cyberark-alternative"
      seoTitle={c.seoTitle}
      seoDescription={c.seoDescription}
      heroEyebrow={c.heroEyebrow}
      heroTitle={
        <>
          {c.heroTitle[0]}
          <br />
          {c.heroTitle[1]}
        </>
      }
      heroBody={c.heroBody}
      reasons={c.reasons}
      tableTitle={
        <>
          {c.tableTitle[0]}
          <br />
          {c.tableTitle[1]}
        </>
      }
      tableBody={c.tableBody}
      rows={c.rows}
      faqs={c.faqs}
    />
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
